import type React from "react";
import { twJoin } from "tailwind-merge";

import { twResolve } from "@/helpers/tw-resolve";
import { isDefined } from "@/helpers/util";
import { Bar, BarChart, XAxis, YAxis } from "@/ui-adapters/Recharts";

import type { ChartBaseProps } from "./types";

type SimpleProgressChartProps = ChartBaseProps;

export function SimpleProgressChart({
  value,
  min = 0,
  max = 100,
  thresholds,
  renderValue = (value) => `${value}`,
}: SimpleProgressChartProps): React.ReactNode {
  const chartData = [
    {
      name: "primary",
      value,
    },
  ];

  return (
    <BarChart
      margin={{
        top: 0,
        right: 0,
        left: 0,
        bottom: -40,
      }}
      className="w-full"
      layout="vertical"
      data={chartData}
      height={56}
      responsive
    >
      <XAxis type="number" domain={[min, max]} hide />
      <YAxis type="category" hide />
      <Bar
        barSize={8}
        dataKey="value"
        // Recharts will skip rendering if value is 0
        minPointSize={Number.MIN_VALUE}
        radius={2}
        label={<BarLabel value={value} renderValue={renderValue} />}
        className={twResolve(
          "fill-background-brand-secondary",
          isDefined(thresholds?.negative) && value <= thresholds.negative && "fill-background-danger",
        )}
        isAnimationActive={false}
        background={<BarBackground />}
      />
    </BarChart>
  );
}

function BarBackground({
  x,
  y,
  height,
  width,
  fill,
  className,
  onClick,
  onMouseEnter,
  onMouseLeave,
}: React.SVGProps<SVGRectElement>) {
  return (
    <rect
      rx={2}
      className={twJoin("fill-border-default", className)}
      {...{ x, y, height, width, fill, onClick, onMouseEnter, onMouseLeave }}
    />
  );
}

function BarLabel({
  x,
  y,
  value,
  renderValue,
  className,
  onClick,
  onMouseEnter,
  onMouseLeave,
}: React.SVGProps<SVGTextElement> & { value: number; renderValue: (value: number) => React.ReactNode }) {
  return (
    <text
      dx={0}
      dy={-8}
      className={twJoin("fill-text-brand-secondary text-figure-lg", className)}
      {...{ x, y, onClick, onMouseEnter, onMouseLeave }}
    >
      {renderValue(value)}
    </text>
  );
}
